import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges } from '@angular/core';
import { CsProcessSpec }                         from '../cs-chat-segments';
import { layoutProcess, ProcessLayout, PositionedNode } from './process-layout';

/**
 * Renders a process/flow diagram (the parsed ```diagram fenced block) as an auto-laid-out SVG:
 * ranked boxes/diamonds with directed connectors, an optional metric chip per node, and a hover
 * tooltip. Presentational only — it takes a validated {@link CsProcessSpec} and computes geometry
 * via {@link layoutProcess}; it never talks to the network. Themed with the shared `--cs-*` tokens
 * so it matches the surrounding chat (and the SVG charts) in light/dark.
 */
@Component({
	standalone:      false,
	selector:        'cs-process-diagram',
	templateUrl:     './cs-process-diagram.component.html',
	styleUrls:       ['./cs-process-diagram.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsProcessDiagramComponent implements OnChanges {

	@Input() spec!: CsProcessSpec;

	layout?: ProcessLayout;

	/** Hover state for the tooltip: the node under the cursor + its screen position within the host. */
	hovered: PositionedNode | null = null;
	tipX = 0;
	tipY = 0;

	constructor(private readonly host: ElementRef<HTMLElement>) {}

	ngOnChanges(): void {
		this.layout = this.spec ? layoutProcess(this.spec) : undefined;
		this.hovered = null;
	}

	/** Inset foreignObject for a decision diamond so the label stays inside the rhombus. */
	labelBox(n: PositionedNode): { x: number; y: number; w: number; h: number } {
		if (n.kind === 'decision') {
			return { x: n.x + n.w * 0.16, y: n.y + n.h * 0.18, w: n.w * 0.68, h: n.h * 0.64 };
		}
		return { x: n.x, y: n.y, w: n.w, h: n.h };
	}

	tooltipText(n: PositionedNode): string {
		return n.tooltip ?? [n.label, n.metric].filter(Boolean).join(' — ');
	}

	onEnter(node: PositionedNode, ev: MouseEvent): void { this.hovered = node; this.position(ev); }
	onMove(ev: MouseEvent): void { if (this.hovered) { this.position(ev); } }
	onLeave(): void { this.hovered = null; }

	private position(ev: MouseEvent): void {
		const rect = this.host.nativeElement.getBoundingClientRect();
		this.tipX = ev.clientX - rect.left;
		this.tipY = ev.clientY - rect.top;
	}

	trackNode = (_: number, n: PositionedNode) => n.id;
}
